/**
 * Historial de Abonos
 *
 * Todos los abonos recibidos, de todos los clientes, en un solo listado.
 * Un abono puede haberse registrado a la cuenta del cliente o contra una
 * factura puntual; el backend unifica ambos sin duplicar los que tienen espejo.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Info } from 'lucide-react'

import api from '@/lib/api'
import {
  cn,
  formatAmountForExport,
  formatClientWithNoteForExport,
  formatCurrency,
  formatDateTime,
} from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { ExportButtons } from '@/components/ui/export-buttons'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface DepositItem {
  id: number
  origin: 'cuenta' | 'factura'
  date: string | null
  has_date: boolean
  client_id: number | null
  client_name: string
  amount: number
  payment_type: string | null
  reference: string | null
  notes: string | null
  client_note: string | null
  user_name: string | null
  applied_bills: string[]
}

interface DepositsHistoryData {
  items: DepositItem[]
  total: number
  page: number
  page_size: number
  pages: number
  total_amount: number
  by_method: Record<string, number>
  undated_count: number
  undated_amount: number
}

const METHOD_KEYS = ['cash', 'card', 'transfer', 'sinpe', 'check'] as const

export function DepositsHistoryReport() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [search, setSearch] = useState('')
  const [method, setMethod] = useState('all')
  const [includeUndated, setIncludeUndated] = useState(false)

  // Se trae todo sin paginar: el export tiene que llevar el rango completo, no
  // solo la primera página. Los filtros de fecha y cliente acotan el volumen.
  const buildParams = () => {
    const params = new URLSearchParams({ all: 'true' })
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', `${dateTo}T23:59:59`)
    if (search.trim()) params.set('search', search.trim())
    if (method !== 'all') params.set('payment_type', method)
    if (includeUndated) params.set('include_undated', 'true')
    return params.toString()
  }

  const { data, isLoading } = useQuery({
    queryKey: ['reports-deposits-history', dateFrom, dateTo, search, method, includeUndated],
    queryFn: async () => {
      const response = await api.get(`/reports/deposits-history?${buildParams()}`)
      return response.data as DepositsHistoryData
    },
  })

  // El alias del cliente ("el del taller") se muestra junto al nombre, igual
  // que en la lista de facturas y en Ventas Detalladas.
  const nombreConNota = (item: DepositItem) =>
    formatClientWithNoteForExport(item.client_name, item.client_note, '—')

  const methodLabel = (value: string | null) => {
    const key = (value || 'cash') as (typeof METHOD_KEYS)[number]
    return METHOD_KEYS.includes(key) ? t(`payment.${key}`) : (value ?? '')
  }

  const exportRows = (data?.items ?? []).map((item) => ({
    fecha: item.has_date && item.date
      ? formatDateTime(item.date)
      : t('reports.depositsNoDate'),
    cliente: nombreConNota(item),
    origen:
      item.origin === 'cuenta'
        ? t('reports.depositOriginAccount')
        : t('reports.depositOriginBill'),
    metodo: methodLabel(item.payment_type),
    aplicado: item.applied_bills.join(', '),
    recibido_por: item.user_name ?? '',
    monto: item.amount,
  }))

  return (
    <div className="space-y-4 pb-8">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/reports')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold truncate">{t('reports.depositsHistory')}</h1>
          <p className="text-xs text-muted-foreground">{t('reports.depositsHistoryDesc')}</p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <DatePicker
              value={dateFrom}
              onChange={setDateFrom}
              placeholder={t('reports.dateFrom')}
            />
            <DatePicker value={dateTo} onChange={setDateTo} placeholder={t('reports.dateTo')} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('reports.depositsSearchClient')}
            />
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger>
                <SelectValue placeholder={t('reports.depositsAllMethods')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('reports.depositsAllMethods')}</SelectItem>
                {METHOD_KEYS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {t(`payment.${key}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ExportButtons
            data={exportRows}
            filename={`historial-abonos${dateFrom ? `-${dateFrom}` : ''}`}
            columns={[
              { key: 'fecha', header: t('reports.depositDate'), asText: true },
              { key: 'cliente', header: t('reports.depositClient') },
              { key: 'origen', header: t('reports.depositOrigin') },
              { key: 'metodo', header: t('reports.depositMethod') },
              { key: 'aplicado', header: t('reports.depositAppliedTo'), asText: true },
              { key: 'recibido_por', header: t('reports.depositReceivedBy') },
              {
                key: 'monto',
                header: t('reports.depositsTotalReceived'),
                format: (v) => formatAmountForExport(Number(v)),
                total: true,
              },
            ]}
          />
        </CardContent>
      </Card>

      {/* Aviso de abonos sin fecha */}
      {!!data?.undated_count && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-300/60 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-500/40 dark:bg-blue-950/40 dark:text-blue-100">
          <Info className="h-4 w-4 shrink-0" />
          <span className="min-w-0">
            {t('reports.depositsUndatedNotice', {
              count: data.undated_count,
              amount: formatCurrency(data.undated_amount),
            })}
          </span>
          {includeUndated ? (
            <span className="text-xs opacity-80">{t('reports.depositsUndatedIncluded')}</span>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setIncludeUndated(true)}>
              {t('reports.depositsUndatedInclude')}
            </Button>
          )}
        </div>
      )}

      {/* Totales */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('reports.depositsTotalReceived')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{formatCurrency(data?.total_amount ?? 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('reports.depositsCount')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{data?.total ?? 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Listado */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : !data?.items.length ? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              {t('common.noData')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-left">
                  <tr>
                    <th className="p-3 font-medium">{t('reports.depositDate')}</th>
                    <th className="p-3 font-medium">{t('reports.depositClient')}</th>
                    <th className="p-3 font-medium">{t('reports.depositOrigin')}</th>
                    <th className="p-3 font-medium">{t('reports.depositMethod')}</th>
                    <th className="p-3 font-medium">{t('reports.depositAppliedTo')}</th>
                    <th className="p-3 text-right font-medium">
                      {t('reports.depositsTotalReceived')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr
                      key={`${item.origin}-${item.id}`}
                      className={cn('border-b last:border-b-0', !item.has_date && 'bg-muted/30')}
                    >
                      <td className="whitespace-nowrap p-3">
                        {item.has_date && item.date ? (
                          formatDateTime(item.date)
                        ) : (
                          <span className="italic text-muted-foreground">
                            {t('reports.depositsNoDate')}
                          </span>
                        )}
                      </td>
                      <td className="p-3">{nombreConNota(item)}</td>
                      <td className="whitespace-nowrap p-3 text-muted-foreground">
                        {item.origin === 'cuenta'
                          ? t('reports.depositOriginAccount')
                          : t('reports.depositOriginBill')}
                      </td>
                      <td className="whitespace-nowrap p-3">{methodLabel(item.payment_type)}</td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {item.applied_bills.join(', ') || '—'}
                      </td>
                      <td className="whitespace-nowrap p-3 text-right font-medium">
                        {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
